import React, { useEffect, useState } from 'react';
import { TopNav } from '../components/TopNav';
import { ActivityFeed } from '../components/ActivityFeed';
import { PerformanceComparator } from '../components/PerformanceComparator';
import { listActivities, createActivity, deleteActivity, type Activity } from '@/services/activities';
import { listPartnerships, createPartnership, deletePartnership, type PartnershipDto } from '@/services/partnerships';

interface PageProps { theme: string; toggleTheme: () => void }

const AnalyticsPage: React.FC<PageProps> = ({ theme, toggleTheme }) => {
  const [activitiesRaw, setActivitiesRaw] = useState<Activity[]>([]);
  const [activities, setActivities] = useState<{ text: string; time: string }[]>([]);
  const [partnerships, setPartnerships] = useState<PartnershipDto[]>([]);
  const [newActText, setNewActText] = useState('');
  const [newActTime, setNewActTime] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newStatus, setNewStatus] = useState('em negociação');

  useEffect(() => {
    (async () => {
      try {
        const a = await listActivities(0, 20);
        setActivitiesRaw(a.items);
        setActivities(a.items.map(x => ({ text: x.text, time: x.timeText || new Date(x.createdAt).toLocaleString() })));
      } catch {}
      try {
        const p = await listPartnerships();
        setPartnerships(p);
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen bg-body">
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <main className="max-w-7xl mx-auto p-3 md:p-4 space-y-4">
        <header>
          <h1 className="text-xl font-extrabold text-primary">Análises</h1>
          <p className="text-sm text-muted">KPIs de relacionamento, atividades e insights.</p>
        </header>
        {/* Quick add Activity */}
        <div className="bg-white dark:bg-[#181818] border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted">Atividade</label>
            <input className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800" value={newActText} onChange={e=>setNewActText(e.target.value)} placeholder="ex.: Reunião com A&R"/>
          </div>
          <div>
            <label className="text-xs text-muted">Quando</label>
            <input className="w-44 px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800" value={newActTime} onChange={e=>setNewActTime(e.target.value)} placeholder="ex.: há 1 hora"/>
          </div>
          <button className="px-3 py-2 rounded-md bg-zinc-900 text-white dark:bg-green-500 dark:text-black" onClick={async()=>{
            if(!newActText) return;
            await createActivity(newActText, newActTime || undefined);
            setNewActText(''); setNewActTime('');
            const a = await listActivities(0,20); setActivitiesRaw(a.items); setActivities(a.items.map(x=>({text:x.text,time:x.timeText||new Date(x.createdAt).toLocaleString()})));
          }}>Adicionar</button>
        </div>
        <PerformanceComparator partnerships={partnerships} />
        <ActivityFeed activities={activities} />
        {/* Simple admin list with delete */}
        {activitiesRaw.length>0 && (
          <div className="bg-white/70 dark:bg-black/30 rounded-lg border border-default p-3">
            <p className="text-sm font-semibold mb-2">Atividades (admin)</p>
            <ul className="text-sm space-y-1">
              {activitiesRaw.map(a => (
                <li key={a.id} className="flex items-center justify-between">
                  <span className="text-muted truncate mr-2">{a.text}</span>
                  <button className="text-red-500 text-xs" onClick={async()=>{ await deleteActivity(a.id); const r=await listActivities(0,20); setActivitiesRaw(r.items); setActivities(r.items.map(x=>({text:x.text,time:x.timeText||new Date(x.createdAt).toLocaleString()}))); }}>excluir</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Quick add Partnership */}
        <div className="bg-white dark:bg-[#181818] border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted">Marca</label>
            <input className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800" value={newBrand} onChange={e=>setNewBrand(e.target.value)} placeholder="ex.: Nike"/>
          </div>
          <div>
            <label className="text-xs text-muted">Status</label>
            <input className="w-44 px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800" value={newStatus} onChange={e=>setNewStatus(e.target.value)} placeholder="ex.: em negociação"/>
          </div>
          <button className="px-3 py-2 rounded-md bg-zinc-900 text-white dark:bg-green-500 dark:text-black" onClick={async()=>{
            if(!newBrand) return;
            await createPartnership({ brandName: newBrand, status: newStatus, details: [{ label: 'Valor estimado', value: '—' }] });
            setNewBrand('');
            const p = await listPartnerships(); setPartnerships(p);
          }}>Adicionar Parceria</button>
        </div>
        {partnerships.length>0 && (
          <div className="bg-white/70 dark:bg-black/30 rounded-lg border border-default p-3">
            <p className="text-sm font-semibold mb-2">Parcerias (admin)</p>
            <ul className="text-sm space-y-1">
              {partnerships.map(p => (
                <li key={p.id} className="flex items-center justify-between">
                  <span className="text-muted truncate mr-2">{p.brandName} — {p.status}</span>
                  <button className="text-red-500 text-xs" onClick={async()=>{ await deletePartnership(p.id); setPartnerships(await listPartnerships()); }}>excluir</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsPage;

